import { ButtonCompound } from "../Button";
import handleStyles from "./DndHandle.module.scss";
import cx from "classnames";
import { forwardRef } from "react";
import { DndHandle } from "./DndHandle";

/**
 * Props for the DndHandleButton component
 * @extends React.ComponentPropsWithoutRef<typeof ButtonCompound>
 */
export interface DndHandleButtonProps
  extends React.ComponentPropsWithoutRef<typeof ButtonCompound> {
  /**
   * Indicates whether the handle is currently being dragged.
   * @default false
   */
  isActive?: boolean;
}

/**
 * A reusable drag handle button component for drag and drop functionality.
 *
 * Features:
 * - Interactive button with drag handle icon
 * - Visual feedback for active drag state
 * - Accessible with proper ARIA attributes
 * - Extends ButtonCompound functionality
 * - Customizable styling through className prop
 * - Supports all standard button props
 * - Consistent visual appearance across applications
 *
 * @example
 * <DndHandleButton
 *   isActive={isDragging}
 *   className="custom-handle-button"
 *   onClick={handleClick}
 * />
 */
export const DndHandleButton = forwardRef<
  HTMLButtonElement,
  DndHandleButtonProps
>(({ isActive = false, className, ...props }, ref) => {
  const handleButtonClasses = cx(className, handleStyles["handle-button"], {
    [handleStyles["active"]]: isActive,
  });

  return (
    <ButtonCompound
      ref={ref}
      className={handleButtonClasses}
      type="button"
      shape="rounded"
      {...props}
    >
      <DndHandle />
    </ButtonCompound>
  );
});

DndHandleButton.displayName = "DndHandleButton";
